
import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Review } from "@/types/reviews";

export interface DeleteStatus {
  success: boolean;
  message: string;
  timestamp: Date;
}

export const useDebugReviewsManagement = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [selectedReview, setSelectedReview] = useState<Review | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [deleteStatus, setDeleteStatus] = useState<DeleteStatus | null>(null);

  // Use a specific debug query key to avoid conflicts with normal reviews
  const { data: reviews, isLoading, refetch } = useQuery({
    queryKey: ["debug-reviews"],
    queryFn: async () => {
      try {
        console.log("[DEBUG] Fetching reviews");
        
        const { data: reviewsData, error: reviewsError } = await supabase
          .from("reviews")
          .select(`
            *,
            services(name)
          `)
          .order("created_at", { ascending: false });
        
        if (reviewsError) {
          console.error("[DEBUG] Error fetching reviews:", reviewsError);
          throw reviewsError;
        }

        console.log("[DEBUG] Raw reviews data:", reviewsData);

        const processedReviews = await Promise.all((reviewsData || []).map(async (review) => {
          let customerName = "Anonymous User";
          
          if (review.customer_id) {
            try {
              const { data: profileData, error: profileError } = await supabase
                .from("profiles")
                .select("display_name")
                .eq("id", review.customer_id)
                .maybeSingle();
              
              if (!profileError && profileData && profileData.display_name) {
                customerName = profileData.display_name;
              }
            } catch (err) {
              console.error("[DEBUG] Error fetching profile:", err);
            }
          }
          
          return {
            ...review,
            service_name: review.services?.name || "Unknown Service",
            customer_name: customerName,
            highlighted: review.highlighted === true
          };
        }));

        console.log("[DEBUG] Processed reviews:", processedReviews);
        return processedReviews as Review[];
      } catch (error) {
        console.error("[DEBUG] Error in reviews query function:", error);
        return [];
      }
    },
  });

  const deleteReviewMutation = useMutation({
    mutationFn: async (id: number) => {
      console.log(`[DEBUG] Attempting to delete review with ID: ${id}`);
      
      const { error, data } = await supabase
        .from("reviews")
        .delete()
        .eq("id", id)
        .select();
      
      if (error) {
        console.error("[DEBUG] Delete error:", error);
        throw error;
      }
      
      console.log("[DEBUG] Delete successful, response:", data);
      return { id, data };
    },
    onMutate: (id) => {
      console.log(`[DEBUG] onMutate called with ID: ${id}`);
      
      // Optimistic update
      const previousReviews = queryClient.getQueryData<Review[]>(["debug-reviews"]);
      console.log("[DEBUG] Previous reviews:", previousReviews);
      
      if (previousReviews) {
        const updatedReviews = previousReviews.filter(review => review.id !== id);
        console.log("[DEBUG] Updated reviews:", updatedReviews);
        queryClient.setQueryData(["debug-reviews"], updatedReviews);
      }
      
      return { previousReviews };
    },
    onSuccess: (result, id) => {
      console.log(`[DEBUG] onSuccess called for ID: ${id}`);
      
      // Close the dialog if it's open
      if (isDetailOpen) {
        setIsDetailOpen(false);
      }
      
      // Invalidate both the debug reviews query and the normal reviews queries
      queryClient.invalidateQueries({ queryKey: ["debug-reviews"] });
      queryClient.invalidateQueries({ queryKey: ["admin-reviews"] });
      queryClient.invalidateQueries({ queryKey: ["review-count"] });
      
      // Set success status
      setDeleteStatus({
        success: true,
        message: `Review #${id} deleted successfully.`,
        timestamp: new Date()
      });
      
      toast({ 
        title: "Success", 
        description: "Review deleted successfully" 
      });
    },
    onError: (error: Error, id) => {
      console.error(`[DEBUG] onError called for ID: ${id}`, error);
      
      // Set error status
      setDeleteStatus({
        success: false,
        message: `Error deleting review #${id}: ${error.message}`,
        timestamp: new Date()
      });
      
      toast({
        variant: "destructive",
        title: "Error",
        description: `Failed to delete review: ${error.message}`,
      });
    },
    onSettled: () => {
      console.log("[DEBUG] onSettled called");
    },
  });

  // Add highlight toggle mutation
  const updateHighlightMutation = useMutation({
    mutationFn: async ({ id, highlighted }: { id: number, highlighted: boolean }) => {
      console.log(`[DEBUG] Updating review ${id} highlight status to ${highlighted}`);
      
      // Be explicit about the data type we're sending to ensure it's properly handled
      const { error, data } = await supabase
        .from("reviews")
        .update({ highlighted: highlighted })
        .eq("id", id)
        .select();
      
      if (error) {
        console.error("[DEBUG] Highlight update error:", error);
        throw error;
      }
      
      console.log("[DEBUG] Highlight update successful, response:", data);
      return { id, data, highlighted };
    },
    onMutate: ({ id, highlighted }) => {
      console.log(`[DEBUG] onMutate called for highlight toggle: ID ${id}, value ${highlighted}`);
      
      // Optimistic update
      const previousReviews = queryClient.getQueryData<Review[]>(["debug-reviews"]);
      console.log("[DEBUG] Previous reviews before highlight change:", previousReviews);
      
      if (previousReviews) {
        const updatedReviews = previousReviews.map(review => 
          review.id === id ? { ...review, highlighted } : review
        );
        console.log("[DEBUG] Updated reviews after highlight change:", updatedReviews);
        queryClient.setQueryData(["debug-reviews"], updatedReviews);
      }
      
      // Update the selected review if it's open
      if (selectedReview && selectedReview.id === id) {
        setSelectedReview({
          ...selectedReview,
          highlighted
        });
      }
      
      return { previousReviews };
    },
    onSuccess: (result, { id, highlighted }) => {
      console.log(`[DEBUG] onSuccess called for highlight toggle: ID ${id}, value ${highlighted}`);
      
      // Invalidate queries
      queryClient.invalidateQueries({ queryKey: ["debug-reviews"] });
      queryClient.invalidateQueries({ queryKey: ["admin-reviews"] });
      
      // If we have a service_id, invalidate that specific service's reviews
      if (selectedReview?.service_id) {
        queryClient.invalidateQueries({ queryKey: ["reviews", selectedReview.service_id] });
      }
      
      toast({ 
        variant: "default",
        title: "Success", 
        description: highlighted 
          ? "Review highlighted successfully" 
          : "Review unhighlighted successfully" 
      });
    },
    onError: (error: Error, { id, highlighted }) => {
      console.error(`[DEBUG] onError called for highlight toggle: ID ${id}, value ${highlighted}`, error);
      
      toast({
        variant: "destructive",
        title: "Error",
        description: `Failed to update review: ${error.message}`,
      });
    }
  });

  // Clear delete status after 5 seconds
  useEffect(() => {
    if (deleteStatus) {
      const timer = setTimeout(() => {
        setDeleteStatus(null);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [deleteStatus]);

  const handleRefresh = () => {
    setIsRefreshing(true);
    console.log("[DEBUG] Manually refreshing data");
    
    refetch().then(() => {
      setIsRefreshing(false);
      console.log("[DEBUG] Data refresh complete");
      
      toast({
        title: "Refreshed",
        description: "Review data has been refreshed"
      });
    });
  };

  const handleViewDetails = (review: Review) => {
    console.log("[DEBUG] Opening details for review:", review);
    setSelectedReview(review);
    setIsDetailOpen(true);
  };

  const handleDeleteReview = (id: number) => {
    console.log(`[DEBUG] Delete requested for review ID: ${id}`);
    
    // Check if this review is the currently selected one
    if (selectedReview && selectedReview.id === id) {
      console.log("[DEBUG] This is the currently selected review");
    }
    
    // Call the delete mutation
    deleteReviewMutation.mutate(id);
  };

  const handleHighlightToggle = (id: number, highlighted: boolean) => {
    console.log(`[DEBUG] Highlight toggle requested for review ID: ${id}, value: ${highlighted}`);
    updateHighlightMutation.mutate({ id, highlighted });
  };

  return {
    isLoading,
    reviews: reviews || [],
    isDetailOpen,
    setIsDetailOpen,
    selectedReview,
    setSelectedReview,
    deleteReviewMutation,
    updateHighlightMutation,
    handleDeleteReview,
    handleHighlightToggle,
    handleRefresh,
    handleViewDetails,
    isRefreshing,
    deleteStatus,
  };
};
